// Taşeron portalı — Panel: "Sahaya hazırlık" (api-contract §28.3 `counts`). Sağ sütunda
// `UpcomingPermitsCard`'ın ÜSTÜNDE (owner isteği, sıra: SiteReadinessCard → UpcomingPermitsCard
// → AwaitingAnalysisCard) — üç segmentli bir çubuk + ALTINDA üç satırlık bir lejant (nokta +
// rakam + etiket), açık iş izinlerinde kapsanan personelin ne kadarının engelli/riskli/temiz
// olduğunu gösterir. Lejant, eski tek satırlık "4 engellendi  8 yaklaşıyor  6 hazır" yazısının
// yerine geçti (owner bulgusu: çıplak sıfatların öznesi yoktu VE orta rakam amber OLAMADIĞI
// için renk↔sayı eşlemesi salt konumsaldı — bkz. aşağıdaki amber notu).
//
// **Partition DOĞRULANDI, tahmin değil.** Backend kanonu
// (`SupplierDashboardService.cs:163-164`):
//   var personnelBlocked = counts.BlockedToday;
//   var personnelReady = Math.Max(0, distinctPersonnel - personnelBlocked);
// `personnelReady` bir TÜMLEYENDİR (temel küme − engelli), pozitif bir hazırlık testi DEĞİL.
// Buradan:
//   - `personnelReady + personnelBlocked === |temel küme|` (§28.1(0)) — TAM bölme, inşa gereği.
//   - `atRiskThisWeek` (§28.1(3)) temel küme içindedir ve "`blockedToday` mutlak kazanır"
//     kuralıyla (§28.1) engellilerle KESİŞMEZ → tamamen `personnelReady`'nin İÇİNDEDİR.
//   - Dolayısıyla `personnelReady - atRiskThisWeek` GÜVENLİDİR (asla negatif olamaz) ve üçlü
//     temel kümeyi çift saymadan böler: engellendi + yaklaşıyor + üçüncü segment === temel küme.
//
// **Üçüncü segment `clear`dır, `ready` DEĞİL — bilinçli isim değişikliği.** `personnelReady -
// atRiskThisWeek` bir POZİTİF hazırlık testi değildir, bir TÜMLEYENİN (`personnelReady`, kendisi
// zaten temel küme − engelli) alt kümesidir; "hazır" etiketi bu yüzden YANLIŞ — bu segment
// sessizce izni HENÜZ BAŞLAMAMIŞ ya da başka bir yoldan yalnızca "engelli değil" sayılan, pozitif
// olarak "hazır" diye doğrulanmamış kişileri de İÇERİR. Doğru okuma "bu hafta engel yok"tur —
// değişken adı, segment anahtarı ve i18n anahtarı bu yüzden `clear`dir. BİR DAHA "hazır"a
// ÇEVRİLMEZ (bkz. hesaplama satırındaki not).
//
// **Payda etiketi ZORUNLU (§28.3, normatif — `SupplierDashboardCountsDto` dosya başı notu,
// `packages/shared/src/api/supplierDashboard.ts`).** `personnelReady + personnelBlocked`
// paydası taşeronun TOPLAM personel mevcudu DEĞİLDİR — açık iş izinlerinde kapsanan TEKİL
// personeldir; sözleşme istemcinin bunu böyle etiketlemesini ZORUNLU kılar (aksi hâlde kullanıcı
// "5 hazır + 2 engelli = 7" derken 40 kişilik ekibini arar). Alt yazı
// (`dashboard.readiness.denominator`) BU YÜZDEN VAR — owner'ın reddettiği ⓘ "bilgi cümlesi"
// DEĞİL, sözleşmenin dayattığı payda etiketi. Owner'ın açıkça istemediği iki şey BİLİNÇLİ
// olarak eklenmedi: açıklama cümlesi ("… sizden işlem beklenmiyor" türü) ve alt bağlantı
// ("Personelim →").
//
// **Üç etiket normatif (`api-contract.md:4974-4984`), stil tercihi DEĞİL.** Satır 4975
// `blockedToday` etiketini AYNEN dayatır ("bugün sahaya giremez"); satır 4978 `atRiskThisWeek`'i
// "belgesi dolacaklar" diye etiketlemeyi AÇIKÇA YASAKLAR (iki dalı — belge bitişi + bu hafta
// başlayan iş — TEK sayıda kaynaştırdığı için, §28.1(3)). Üç metin de `turkish-ohs-expert`
// tarafından gözden geçirildi; stil kaygısıyla YENİDEN YAZILMAZ.
//
// ⚠️ **Amber (`text-warning`/`text-warning-foreground`) BAĞIMSIZ metin olarak KULLANILMAZ** —
// depo kuralı (bkz. UpcomingPermitsCard.tsx dosya başı notu): koyu temada `bg-surface` üstünde
// kaybolur. Amber YALNIZ dolgu ZEMİNİ olarak yaşar (çubuğun kendi segmenti `bg-warning`, lejant
// noktası aynı sınıf) — lejantın nokta + rakam + etiket düzeni TAM OLARAK bu yüzden var: orta
// kovanın (`atRisk`) rengini nokta taşır, rakam üç satırda da `text-fg`'dir, yasaklı amber metin
// hiç oluşmaz. Aynı desen (`bg-warning` nokta + `text-fg` sayı) `CompanySwitcher.tsx`'te zaten
// var; owner'ın mock'unda ortadaki rakam amberdi — bu BİLİNÇLİ bir sapma, yeni bir istisna değil.

import type { SupplierDashboardDto } from '@isgportal/shared';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DashboardPanel, PanelHeader } from './PanelHeader';

export function SiteReadinessCard({ dto }: { dto: SupplierDashboardDto }) {
  const { t } = useTranslation();
  const { personnelBlocked, personnelReady, atRiskThisWeek } = dto.counts;

  // Temel küme (§28.1(0)) — açık iş izinlerinde kapsanan tekil personel. Sıfırsa (açık izin ya
  // da kapsanan personel yok) gösterilecek bir küme yok; boş çubuk çizmek yerine kart hiç
  // render edilmez.
  const total = personnelReady + personnelBlocked;
  if (total === 0) {
    return null;
  }

  // Üçüncü segment — dosya başı notundaki partition kanıtı: `atRiskThisWeek` DAİMA
  // `personnelReady`'nin bir alt kümesidir, bu yüzden fark asla negatif olamaz. `clear` diye
  // adlandırılır, `ready` DEĞİL: `personnelReady` kendisi bir TÜMLEYEN (temel küme − engelli),
  // pozitif bir hazırlık testi değil — bu fark sessizce izni henüz başlamamış kişileri de
  // içerebilir, bu yüzden "hazır" YANLIŞ bir iddiadır (dosya başı notu, "Üçüncü segment `clear`
  // dır" paragrafı). Bir daha `ready`/"hazır"a ÇEVRİLMEZ.
  const clear = personnelReady - atRiskThisWeek;

  const segments: { key: string; count: number; barClassName: string }[] = [
    { key: 'blocked', count: personnelBlocked, barClassName: 'bg-danger' },
    { key: 'atRisk', count: atRiskThisWeek, barClassName: 'bg-warning' },
    { key: 'clear', count: clear, barClassName: 'bg-success' },
  ];

  return (
    <DashboardPanel>
      <PanelHeader icon={Users} title={t('dashboard.readiness.title')} />

      <div className="flex flex-col gap-2 px-4 py-4">
        {/* Çubuk — segment genişlikleri çalışma zamanı verisinden ORANTILI kurulur; bu yüzden
            Tailwind sınıfı DEĞİL `style.width` kullanılır (statik tarama yüzde değerini
            GÖREMEZ). S37-3: çubuk KARE (Carbon), segmentler arası 1px `bg-surface` aralık (gap-px). Sıfır sayan segment HİÇ render edilmez — 0
            genişlikli bir şerit görsel gürültüden başka bir şey katmaz. `aria-hidden` — sayılar
            zaten aşağıda metin olarak basılıyor. */}
        <div className="flex h-4 w-full gap-px overflow-hidden bg-surface" aria-hidden="true">
          {segments.map((segment) =>
            segment.count > 0 ? (
              <div
                key={segment.key}
                className={segment.barClassName}
                style={{ width: `${(segment.count / total) * 100}%` }}
              />
            ) : null,
          )}
        </div>

        {/* Lejant — nokta + rakam + etiket, sabit sırada (blocked → atRisk → clear), HER ZAMAN
            üçü de basılır (sıfır sayan segmentler dahil) — kart yenilendiğinde satır sayısı
            değişip yükseklik zıplamasın diye. Bu, yukarıdaki çubuğun "sıfır segmenti hiç
            render etme" davranışından KASITLI olarak farklıdır (çubuk görsel, lejant metin).
            Renk artık NOKTADADIR (dosya başı amber notu) — rakamların üçü de `text-fg`. */}
        <div className="flex flex-col gap-1">
          {segments.map((segment) => (
            <div key={segment.key} className="flex items-center gap-2 text-sm">
              <span aria-hidden="true" className={`h-3 w-3 shrink-0 ${segment.barClassName}`} />
              <span className="min-w-6 shrink-0 text-right font-semibold tnum text-fg">
                {segment.count}
              </span>
              <span className="min-w-0 text-muted">{t(`dashboard.readiness.${segment.key}`)}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted">
          {t('dashboard.readiness.denominator', { count: total })}
        </p>
      </div>
    </DashboardPanel>
  );
}
